package org.vreo.devicehub.features.core.domain.device.device

/**
 * Product type, defines available products that can be used with this API (tensor). This allows
 * to locate or get product using only [productNumber], which consists of
 * [deviceType]-[model]-[productNumber]-unique-identifier.
 *
 * @property productNumber unique product number identifier
 * @property deviceType device type/name
 * @property model device model
 */
sealed class ProductType(
    val productNumber: String,
    val deviceType: String,
    val model: String
) {
    data object VoltS1PoleShortRange : ProductType(
        productNumber = "VOLTS1PSR",
        deviceType = "volt",
        model = "s1-pole-short-range"
    )

    data object VoltS1PoleLongRange : ProductType(
        productNumber = "VOLTS1PLR",
        deviceType = "volt",
        model = "s1-pole-long-range"
    )

    data object VoltS1Bollard : ProductType(
        productNumber = "VOLTS1B",
        deviceType = "volt",
        model = "s1-bollard"
    )

    data object VoltS1WallMount : ProductType(
        productNumber = "VOLTS1WM",
        deviceType = "volt",
        model = "s1-wall-mount"
    )

    // Define the list of all available products in the companion object
    companion object {
        /**
         * A list containing all available [ProductType] instances for easy iteration.
         */
        val VALUES: List<ProductType> by lazy {
            listOf(
                VoltS1PoleShortRange,
                VoltS1PoleLongRange,
                VoltS1Bollard,
                VoltS1WallMount
            )
        }

        /**
         * A lookup map for quickly finding a product by its unique product number.
         */
        private val productMap: Map<String, ProductType> by lazy { VALUES.associateBy { it.productNumber } }

        /**
         * Retrieves a [ProductType] instance based on its unique product number.
         */
        fun getByProductNumber(number: String): ProductType? {
            return productMap[number]
        }
    }
}